// src/oura/merchant/YourRequests.tsx
//
// "Your requests" on Work, under Ask for a change: everything the business
// started (YW, 29 Sep 2026), the changes they sent and the calls they asked
// for. Two groups: With us, open (sent, replied to and not done, a small
// change, a call open or booked), and Done, folded. A row is 1 line: what
// they asked, and one fact ("Angel replied Sun 27 Sep"). The full text, the
// files and our reply are in the request's drawer (RequestDrawer), which
// the row opens and which offers "Ask a follow-up".
//
// `missingNotice`: the bell's "{name} replied to your request" linked here
// to a request the list does not have (or could not read). The card says who
// replied and where the full reply is, from the notice's own words.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { RequestSheet } from "./drawers/RequestDrawer";
import type { ChangeAbout } from "./drawers/ChangeRequestDrawer";
import { clientArea } from "../merchantV2";
import { MessageSquarePlusIcon, MessagesSquareIcon, PhoneIcon } from "lucide-react";
import { AREA_TEXT, CardEmpty, FOCUS_RING, FoldCard, GroupFold, ListRow, PillButton, ShowMore, TAP_TARGET, TYPE, Tag } from "@oura/ui";
import { trimText } from "@oura/docs";
import { cn } from "../lib/cn";
import { ReplyPictureGrid } from "../components/ReplyPictures";
import { dayDate, initials } from "../lib/oura";
import type { MyRequestsRead } from "../hooks/useMyChangeRequests";
import {
    MY_CALL_STATE,
    MY_REQUEST_GROUP,
    OURA_INBOX,
    extraSmallChanges,
    myCallFact,
    myChangeFact,
    myRequestGroup,
    smallChangeTag,
    withUsCount,
    type MyRequestGroup,
    type MyRequestRow,
    type ReplyNote,
} from "./myRequests";

/** Requests shown in a group before "Show N more". */
const SHOWN = 3;
const GROUP_ORDER: MyRequestGroup[] = ["with_us", "done"];

export function YourRequestsCard({
    read,
    rows,
    onRetry,
    onOpen,
    manager,
    email,
    missingNotice,
    inPlace,
    bare,
    untitled,
}: {
    read: Exclude<MyRequestsRead, { state: "notServed" }>;
    /** The changes and calls together, newest first; null until read. */
    rows: MyRequestRow[] | null;
    onRetry: () => void;
    onOpen: (id: string) => void;
    /** The account manager from the payload, or null when nobody is assigned. */
    manager: string | null;
    /** The address replies come from (`contactEmail`), or null. */
    email: string | null;
    /** A linked request the list does not have: who replied, from the bell's notice. */
    missingNotice: ReplyNote | null;
    /** Opening a row in place needs what the drawer needed (#4062 MR-FE-7). */
    inPlace?: {
        viewer: string;
        canFollowUp: boolean;
        onFollowUp: (about: ChangeAbout) => void;
        /**
         * The follow-up form, drawn under the reply it follows (16 Oct 2026),
         * in the account manager card; `close` folds it away.
         */
        renderFollowUp?: (about: ChangeAbout, close: () => void) => ReactNode;
    };
    /** No card of its own: a section inside the account manager card (16 Oct 2026). */
    bare?: boolean;
    /** Bare, under a card head that already says "Your requests" (Our work, 27 Oct 2026). */
    untitled?: boolean;
}) {
    const [openId, setOpenId] = useState<string | null>(null);
    const extra = read.state === "ready" ? extraSmallChanges(read.requests) : new Set<string>();
    const withUs = read.state === "ready" ? withUsCount(read.requests, read.calls) : 0;
    const grouped: Record<MyRequestGroup, MyRequestRow[]> = { with_us: [], done: [] };
    for (const r of rows ?? []) grouped[myRequestGroup(r)].push(r);
    const row = (r: MyRequestRow) =>
        // In the account manager card, a change reads as a conversation.
        bare && inPlace && r.kind === "change" ? (
            <RequestThread
                key={r.id}
                row={r}
                extra={extra.has(r.id)}
                manager={manager}
                canFollowUp={inPlace.canFollowUp}
                onFollowUp={inPlace.onFollowUp}
                renderFollowUp={inPlace.renderFollowUp}
            />
        ) : inPlace ? (
            <OpenableRequest
                key={r.id}
                row={r}
                extra={extra.has(r.id)}
                open={openId === r.id}
                onToggle={() => setOpenId((id) => (id === r.id ? null : r.id))}
                onCollapse={() => setOpenId(null)}
                manager={manager}
                email={email}
                inPlace={inPlace}
            />
        ) : (
            <RequestRow key={r.id} row={r} extra={extra.has(r.id)} onOpen={onOpen} />
        );

    const body = (
        <>
            {missingNotice && <LinkedReplyNote notice={missingNotice} manager={manager} email={email} className="mb-3" />}
            {read.state === "loading" ? (
                <p className={cn(TYPE.tc, "m-0 text-gray-500")} role="status">
                    Loading your requests
                </p>
            ) : read.state === "failed" ? (
                <div>
                    <p className={cn(TYPE.tb, "m-0 text-gray-900")}>Your requests could not be loaded.</p>
                    <PillButton variant="secondary" size="sm" onClick={onRetry} className="mt-2">
                        Try again
                    </PillButton>
                </div>
            ) : rows && rows.length === 0 ? (
                <CardEmpty icon={MessagesSquareIcon} title="No requests yet">
                    Ask us for a change any time, and follow it here.
                </CardEmpty>
            ) : rows ? (
                <>
                    {/* The groups are the only children of this box, so the first loses its divider. */}
                    <div>
                        {GROUP_ORDER.filter((g) => grouped[g].length > 0).map((g) => {
                            const list = grouped[g];
                            return (
                                <GroupFold
                                    key={g}
                                    id={`requests.${g}`}
                                    lead="icon"
                                    tone={MY_REQUEST_GROUP[g].tone}
                                    title={MY_REQUEST_GROUP[g].label}
                                    count={list.length}
                                    defaultFolded={g === "done"}
                                    size={bare ? "sm" : "md"}
                                >
                                    <div>{list.slice(0, SHOWN).map(row)}</div>
                                    <ShowMore count={list.length - SHOWN}>{list.slice(SHOWN).map(row)}</ShowMore>
                                </GroupFold>
                            );
                        })}
                    </div>
                    {read.state === "ready" && read.refreshFailed && (
                        <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")}>
                            Could not check for new replies just now.{" "}
                            <button type="button" onClick={onRetry} className={cn("rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}>
                                Try again
                            </button>
                        </p>
                    )}
                </>
            ) : null}
        </>
    );
    if (bare) {
        return (
            <section id="requests" aria-labelledby={untitled ? undefined : "requests-head"} aria-label={untitled ? "Your requests" : undefined}>
                {!untitled && (
                    <p id="requests-head" className={cn(TYPE.lab, "m-0 mb-2")}>
                        Your requests
                    </p>
                )}
                {body}
            </section>
        );
    }
    return (
        <FoldCard
            id="requests"
            title="Your requests"
            icon={{ icon: MessagesSquareIcon, area: clientArea("people") }}
            foldedFact={rows && rows.length > 0 ? (withUs > 0 ? `${withUs} with us` : "Nothing with us") : undefined}
        >
            {body}
        </FoldCard>
    );
}

/**
 * One request: what they asked, and one fact. A change says who replied, or
 * when it was done or sent; a call when it was sent. A tag only for what the
 * group head does not say: a small change, a booked call.
 */
/**
 * A request row that opens where it is (#4062 MR-FE-7).
 *
 * The drawer covered the page to show a reply and one button. The same body
 * renders under the row; `RequestDrawer` still serves the switch-off path and
 * the `?request=` deep link.
 */
/**
 * A request as a conversation (16 Oct 2026): what they asked, dated the day
 * they asked it, then our reply under it, dated the day we replied. Nothing
 * is said twice: the row is the request, not a heading over a copy of it.
 */
function RequestThread({
    row,
    extra,
    manager,
    canFollowUp,
    onFollowUp,
    renderFollowUp,
}: {
    row: Extract<MyRequestRow, { kind: "change" }>;
    extra: boolean;
    manager: string | null;
    canFollowUp: boolean;
    onFollowUp: (about: ChangeAbout) => void;
    renderFollowUp?: (about: ChangeAbout, close: () => void) => ReactNode;
}) {
    const [replying, setReplying] = useState(false);
    const r = row.change;
    const tag = smallChangeTag(r, extra);
    const asker = r.requestedBy?.trim() || "You";
    const replier = r.reply?.by?.trim() || manager || "ourteam";
    return (
        <article data-request-id={r.id} className="border-t border-gray-100 py-4 first:border-t-0 first:pt-1">
            <div className="flex items-start gap-3">
                <span aria-hidden className={cn(TYPE.tm, "grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gray-100 font-semibold text-gray-600")}>
                    {initials(asker)}
                </span>
                <div className="min-w-0 flex-1">
                    <p className={cn(TYPE.tc, "m-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-500")}>
                        <span className="font-medium text-gray-900">{asker}</span>
                        <span className="tabular-nums">asked {dayDate(r.at)}</span>
                        {tag && <Tag tone={tag.tone}>{tag.label}</Tag>}
                        {r.status === "done" && <Tag tone="green">Done{r.doneAt ? ` ${dayDate(r.doneAt)}` : ""}</Tag>}
                    </p>
                    <p className={cn(TYPE.tb, "m-0 mt-1 whitespace-pre-line break-words text-gray-900")}>{r.note}</p>
                    {r.files.length > 0 && (
                        <ul className="list-plain m-0 mt-2 flex flex-wrap gap-2 p-0">
                            {r.files.map((f, i) => (
                                <li key={`${f.name}-${i}`}>
                                    <a href={f.url} target="_blank" rel="noopener noreferrer" className={cn(TYPE.tc, "rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}>
                                        {f.name}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
            {r.reply ? (
                <div className="ml-11 mt-3 flex items-start gap-3 rounded-xl bg-gray-50 p-3">
                    <span aria-hidden className={cn(TYPE.tm, "grid h-8 w-8 shrink-0 place-items-center rounded-full font-semibold", "bg-blue-100 text-blue-700")}>
                        {initials(replier)}
                    </span>
                    <div className="min-w-0 flex-1">
                        <p className={cn(TYPE.tc, "m-0 flex flex-wrap items-center gap-x-2 text-gray-500")}>
                            <span className="font-medium text-gray-900">{replier}</span>
                            <span className="tabular-nums">replied {dayDate(r.reply.at)}</span>
                        </p>
                        <p className={cn(TYPE.tb, "m-0 mt-1 whitespace-pre-line break-words text-gray-900")}>{r.reply.body}</p>
                        <ReplyPictureGrid files={r.reply.files} />
                    </div>
                </div>
            ) : (
                <p className={cn(TYPE.tc, "m-0 ml-11 mt-2 text-gray-500")}>Waiting for {manager ? `${manager}'s` : "our"} reply. It comes by email too.</p>
            )}
            {canFollowUp && replying && renderFollowUp ? (
                // The reply box, right under the conversation it adds to.
                <div className="ml-11 mt-3">{renderFollowUp({ id: r.id, at: r.at, note: r.note }, () => setReplying(false))}</div>
            ) : canFollowUp ? (
                <p className="m-0 ml-11 mt-2">
                    <button
                        type="button"
                        onClick={() => (renderFollowUp ? setReplying(true) : onFollowUp({ id: r.id, at: r.at, note: r.note }))}
                        className={cn(TYPE.tc, "rounded font-medium text-blue-600 hover:underline", FOCUS_RING, TAP_TARGET)}
                    >
                        Ask a follow-up
                    </button>
                </p>
            ) : null}
        </article>
    );
}

function OpenableRequest({
    row,
    extra,
    open,
    onToggle,
    onCollapse,
    manager,
    email,
    inPlace,
}: {
    row: MyRequestRow;
    extra: boolean;
    open: boolean;
    onToggle: () => void;
    /** Fold this row before something else takes the screen. */
    onCollapse: () => void;
    manager: string | null;
    email: string | null;
    inPlace: { viewer: string; canFollowUp: boolean; onFollowUp: (about: ChangeAbout) => void };
}) {
    return (
        <div className="border-t border-gray-100 first:border-t-0">
            <RequestRow row={row} extra={extra} onOpen={onToggle} />
            {open && (
                <div className="pb-3 pl-6 pr-1">
                    <RequestSheet
                        row={row}
                        extra={extra}
                        viewer={inPlace.viewer}
                        manager={manager}
                        email={email}
                        canFollowUp={inPlace.canFollowUp}
                        // The row folds before Ask for a change opens, so
                        // closing that does not land back on an expanded row
                        // the reader has finished with (Jack, #4085). The
                        // drawer closed itself here; in place, nothing does
                        // it for us.
                        onFollowUp={(about) => {
                            onCollapse();
                            inPlace.onFollowUp(about);
                        }}
                    />
                </div>
            )}
        </div>
    );
}

function RequestRow({ row, extra, onOpen }: { row: MyRequestRow; extra: boolean; onOpen: (id: string) => void }) {
    const group = MY_REQUEST_GROUP[myRequestGroup(row)].label;
    if (row.kind === "change") {
        const r = row.change;
        const tag = smallChangeTag(r, extra);
        const note = trimText(r.note, 140);
        return (
            <ListRow
                data-request-id={r.id}
                lead="icon"
                stackMeta
                icon={<MessageSquarePlusIcon aria-hidden size={14} strokeWidth={2} className={AREA_TEXT.people} />}
                title={`“${note}”`}
                srStatus={group}
                tags={tag ? <Tag tone={tag.tone}>{tag.label}</Tag> : undefined}
                meta={myChangeFact(r) ?? undefined}
                onClick={() => onOpen(r.id)}
            />
        );
    }
    const c = row.call;
    return (
        <ListRow
            data-request-id={c.id}
            lead="icon"
            stackMeta
            icon={<PhoneIcon aria-hidden size={14} strokeWidth={2} className={AREA_TEXT.people} />}
            title={c.about ? `Call about “${c.about}”` : "A call with us"}
            srStatus={group}
            tags={c.status === "scheduled" ? <Tag tone={MY_CALL_STATE.scheduled.tone}>{MY_CALL_STATE.scheduled.label}</Tag> : undefined}
            meta={myCallFact(c) ?? undefined}
            onClick={() => onOpen(c.id)}
        />
    );
}

/**
 * Who replied, and that the full reply is in their email: for a bell link to
 * a request Work cannot show (not in the list, or the list is not read).
 */
export function LinkedReplyNote({
    notice,
    manager,
    email,
    className,
}: {
    notice: ReplyNote | null;
    manager: string | null;
    email: string | null;
    className?: string;
}) {
    const ref = useRef<HTMLDivElement>(null);
    // After the card has unfolded (the reveal puts focus on its head first).
    useEffect(() => {
        const t = window.setTimeout(() => ref.current?.focus({ preventScroll: true }), 350);
        return () => window.clearTimeout(t);
    }, []);
    const who = notice?.title ? notice.title.replace(/[.!?]?$/, ".") : `${manager ?? "Your account manager"} replied to your request.`;
    return (
        <div ref={ref} role="status" tabIndex={-1} className={cn(TYPE.tc, "rounded-xl bg-blue-50 px-3 py-2.5 text-gray-900 outline-none", FOCUS_RING, className)}>
            <p className="m-0 font-medium">{who}</p>
            {notice?.body && <p className="m-0 mt-1 line-clamp-3 break-words text-gray-600">&ldquo;{notice.body}&rdquo;</p>}
            <p className="m-0 mt-1 text-gray-600">The full reply is in your email from {email ?? OURA_INBOX}. Reply there to carry on.</p>
        </div>
    );
}
